import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

const v = (name: string): string => `var(${name})`;

const brand = v('--venue-brand');
const brandHover = v('--venue-brand-hover');
const brandActive = v('--venue-brand-active');
const brandTint = v('--venue-brand-tint');
const brandTint2 = v('--venue-brand-tint-2');
const brandInk = v('--venue-brand-ink');

/* Brand text drawn ON a brand tint. In light mode the brand itself is dark
   enough; on the dark tint it is not, so the dark scheme steps up the ramp. */
const brandOnTint = v('--fb-blue-400');

const page = v('--th-page');
const surface = v('--th-surface');
const surface2 = v('--th-surface-2');
const surfaceSunk = v('--th-surface-sunk');

const border = v('--th-border');
const borderStr = v('--th-border-strong');

const text = v('--th-text');
const text2 = v('--th-text-2');
const textMuted = v('--th-text-muted');

const shadowSm = v('--th-shadow-sm');
const shadowLg = v('--th-shadow-lg');

const dur = v('--fb-dur');

export const FunbutlerPreset = definePreset(Aura, {
    semantic: {
        transitionDuration: dur,

        focusRing: {
            width: '2px',
            style: 'solid',
            color: '{primary.color}',
            offset: '2px',
            shadow: 'none'
        },

        primary: {
            50: brandTint,
            100: brandTint,
            200: brandTint2,
            300: v('--fb-blue-300'),
            400: v('--fb-blue-400'),
            500: brand,
            600: brandHover,
            700: brandActive,
            800: v('--fb-blue-800'),
            900: v('--fb-blue-900'),
            950: v('--fb-blue-900')
        },

        colorScheme: {
            light: {
                primary: {
                    color: brand,
                    contrastColor: brandInk,
                    hoverColor: brandHover,
                    activeColor: brandActive
                },
                highlight: {
                    background: brandTint,
                    focusBackground: brandTint2,
                    color: brand,
                    focusColor: brandHover
                },
                surface: {
                    0: v('--fb-gray-0'),
                    50: v('--fb-gray-50'),
                    100: v('--fb-gray-100'),
                    200: v('--fb-gray-200'),
                    300: v('--fb-gray-300'),
                    400: v('--fb-gray-400'),
                    500: v('--fb-gray-500'),
                    600: v('--fb-gray-600'),
                    700: v('--fb-gray-700'),
                    800: v('--fb-gray-800'),
                    900: v('--fb-gray-900'),
                    950: v('--fb-gray-900')
                },
                formField: {
                    background: surface,
                    disabledBackground: surfaceSunk,
                    filledBackground: surface2,
                    // v22 added a distinct filled-hover token; without it,
                    // filled inputs flash to Aura's grey surface on hover.
                    filledHoverBackground: surface2,
                    filledFocusBackground: surface,
                    borderColor: borderStr,
                    hoverBorderColor: '{primary.color}',
                    focusBorderColor: '{primary.color}',
                    invalidBorderColor: '{red.500}',
                    color: text,
                    disabledColor: textMuted,
                    placeholderColor: textMuted,
                    invalidPlaceholderColor: '{red.500}',
                    floatLabelColor: textMuted,
                    floatLabelFocusColor: '{primary.color}',
                    floatLabelActiveColor: textMuted,
                    floatLabelInvalidColor: '{red.500}',
                    iconColor: textMuted,
                    shadow: shadowSm
                },
                text: {
                    color: text,
                    hoverColor: text,
                    mutedColor: textMuted,
                    hoverMutedColor: text2
                },
                content: {
                    background: surface,
                    hoverBackground: surface2,
                    borderColor: border,
                    color: text,
                    hoverColor: text
                },
                overlay: {
                    select: { background: surface, borderColor: border, color: text },
                    popover: { background: surface, borderColor: border, color: text, shadow: shadowLg },
                    modal: { background: surface, borderColor: border, color: text, shadow: shadowLg },
                    navigation: { shadow: shadowLg }
                },
                // v22: list option tokens are NESTED under `list.option.*`
                // (previously flat `list.optionFocusBackground`). The flat form
                // no longer overrides Aura, which is why option hovers regressed.
                list: {
                    option: {
                        focusBackground: surface2,
                        selectedBackground: brandTint,
                        selectedFocusBackground: brandTint2,
                        color: text,
                        focusColor: text,
                        selectedColor: brand,
                        selectedFocusColor: brandHover
                    }
                },
                // v22: navigation item tokens are NESTED under `navigation.item.*`.
                // (Old `submenuBackground/BorderColor/Shadow` are no longer consumed
                //  by any component — submenu panels inherit `content`/`overlay`.)
                navigation: {
                    item: {
                        focusBackground: surface2,
                        activeBackground: brandTint,
                        color: text2,
                        activeColor: brand,
                        focusColor: text
                    }
                }
            },

            // Dark — same variable references; the variables flip when
            // [data-theme="dark"] is set on <html> via funbutler.theme.css
            dark: {
                primary: {
                    color: brand,
                    contrastColor: brandInk,
                    hoverColor: brandHover,
                    activeColor: brandActive
                },
                highlight: {
                    background: brandTint,
                    focusBackground: brandTint2,
                    color: brandOnTint,
                    focusColor: brandOnTint
                },
                // The ramp stays light-to-dark in BOTH schemes, exactly as Aura
                // declares it. It is tempting to invert it here so that a low
                // number keeps meaning "subtle", but PrimeNG components pick a
                // surface with light-dark({surface.50}, {surface.900}) and rely
                // on 900 being the dark end. Inverting it painted the sidebar
                // layout and main area near-white in dark mode. Everything that
                // genuinely differs per scheme is a --th-* token below, and
                // those flip on their own.
                surface: {
                    0: v('--fb-gray-0'),
                    50: v('--fb-gray-50'),
                    100: v('--fb-gray-100'),
                    200: v('--fb-gray-200'),
                    300: v('--fb-gray-300'),
                    400: v('--fb-gray-400'),
                    500: v('--fb-gray-500'),
                    600: v('--fb-gray-600'),
                    700: v('--fb-gray-700'),
                    800: v('--fb-gray-800'),
                    900: v('--fb-gray-900'),
                    950: v('--fb-gray-900')
                },
                formField: {
                    background: surface,
                    disabledBackground: surfaceSunk,
                    filledBackground: surface2,
                    filledHoverBackground: surface2,
                    filledFocusBackground: surface,
                    borderColor: borderStr,
                    hoverBorderColor: '{primary.color}',
                    focusBorderColor: '{primary.color}',
                    invalidBorderColor: '{red.500}',
                    color: text,
                    disabledColor: textMuted,
                    placeholderColor: textMuted,
                    invalidPlaceholderColor: '{red.500}',
                    floatLabelColor: textMuted,
                    floatLabelFocusColor: '{primary.color}',
                    floatLabelActiveColor: textMuted,
                    floatLabelInvalidColor: '{red.500}',
                    iconColor: textMuted,
                    shadow: shadowSm
                },
                text: {
                    color: text,
                    hoverColor: text,
                    mutedColor: textMuted,
                    hoverMutedColor: text2
                },
                content: {
                    background: surface,
                    hoverBackground: surface2,
                    borderColor: border,
                    color: text,
                    hoverColor: text
                },
                overlay: {
                    select: { background: surface, borderColor: border, color: text },
                    popover: { background: surface, borderColor: border, color: text, shadow: shadowLg },
                    modal: { background: surface, borderColor: border, color: text, shadow: shadowLg },
                    navigation: { shadow: shadowLg }
                },
                list: {
                    option: {
                        focusBackground: surface2,
                        selectedBackground: brandTint,
                        selectedFocusBackground: brandTint2,
                        color: text,
                        focusColor: text,
                        selectedColor: brandOnTint,
                        selectedFocusColor: brandOnTint
                    }
                },
                navigation: {
                    item: {
                        focusBackground: surface2,
                        activeBackground: brandTint,
                        color: text2,
                        activeColor: brandOnTint,
                        focusColor: text
                    }
                }
            }
        }
    },

    components: {
        // The application shell is the one place where a PrimeNG component
        // paints a full-viewport area, so its two largest surfaces have to be
        // the page and card colours rather than Aura's generic grey steps.
        // Aura defaults these to light-dark({surface.50}, {surface.900}), which
        // is a near-match in light mode and visibly wrong against --th-page.
        sidebar: {
            layout: {
                background: page
            },
            main: {
                background: page,
                floatingBackground: surface,
                insetBackground: surface,
                shadow: shadowSm
            }
        },

        toggleswitch: {
            root: {
                width: '38px',
                height: '21px',
                borderRadius: '11px',
                slideDuration: '0.15s'
            },
            handle: {
                size: '15px'
            }
        }
    }
});

/**
 * @summary PrimeNG runtime configuration shared by the whole client app.
 * @description Two options here are load-bearing and were absent from the old
 *              client configuration:
 *
 *              darkModeSelector must match the selector globals.css and
 *              styles.scss use, otherwise PrimeNG's dark tokens and the app's
 *              own dark tokens disagree and half the UI flips while the other
 *              half does not.
 *
 *              cssLayer is deliberately NOT set. It sounds like the right
 *              thing — it orders Tailwind utilities above PrimeNG instead of
 *              leaving the two to stylesheet order — but it puts PrimeNG into
 *              a cascade layer, and layered CSS loses to unlayered CSS no
 *              matter how specific it is.
 *
 *              This application serves a large unlayered legacy stylesheet
 *              (styles.scss, which pulls in bootstrap-reboot, the indigo-pink
 *              Material theme and the styleguide). Layering PrimeNG puts the
 *              component library underneath all of it. Bootstrap's reboot alone
 *              carries `button { border-radius: 0 }`, which silently squared
 *              off every PrimeNG button: previously `.p-button` outranked it on
 *              specificity, and once layered it could not.
 *
 *              Leave PrimeNG unlayered so specificity decides, as the rest of
 *              the application was built against. Where a Tailwind utility has
 *              to win on a PrimeNG element, mark it important (`p-2!`) or set
 *              the matching token in the preset above.
 *
 * @author Simon Gustavsson
 * @since 2026-08-27
 */
export const funbutlerPrimeNGProviders = [
    provideAnimationsAsync(),
    providePrimeNG({
        ripple: true,
        theme: {
            preset: FunbutlerPreset,
            options: {
                darkModeSelector: '[data-theme="dark"]',
                cssLayer: {
                    name: 'primeng',
                    order: 'theme, base, primeng, components, utilities'
                }
            }
        },
        license:
            'eyJpZCI6IjY2ZjEzOGU5LTI1MWQtNDkxYy05Zjk2LWJmNWQyZGIyM2VkZiIsInByb2R1Y3QiOiJwcmltZXVpIiwidGllciI6ImNvbW11bml0eSIsInR5cGUiOiJkZXYiLCJpYXQiOjE3ODI3MzMyMDQsImV4cCI6MTgxNDI2OTIwNH0.a2e8DsxDdCtDSjNuT8v1nbngknhS_ydupL9xcm2gRQP7jPN9SQsp-YX_CK8wtbuIrznljQR85fVi3_RWUAf7Dw'
    })
];

export interface VenueThemeOverrides {
    brand?: string;
    brandHover?: string;
    brandActive?: string;
    brandTint?: string;
    brandTint2?: string;
    brandInk?: string;
    fontDisplay?: string;
    fontBody?: string;
    radius?: string;
    radiusMd?: string;
    radiusLg?: string;
    radiusXl?: string;
    [cssVar: string]: string | undefined;
}

/**
 *
 */
export function applyVenueTheme(overrides: VenueThemeOverrides, root: HTMLElement = document.documentElement): void {
    const map: Record<string, string | undefined> = {
        '--venue-brand': overrides.brand,
        '--venue-brand-hover': overrides.brandHover,
        '--venue-brand-active': overrides.brandActive,
        '--venue-brand-tint': overrides.brandTint,
        '--venue-brand-tint-2': overrides.brandTint2,
        '--venue-brand-ink': overrides.brandInk,
        '--venue-font-display': overrides.fontDisplay,
        '--venue-font-body': overrides.fontBody,
        '--venue-radius': overrides.radius,
        '--venue-radius-md': overrides.radiusMd,
        '--venue-radius-lg': overrides.radiusLg,
        '--venue-radius-xl': overrides.radiusXl,
        ...Object.fromEntries(Object.entries(overrides).filter(([k]) => k.startsWith('--')))
    };
    for (const [prop, value] of Object.entries(map)) {
        if (value !== undefined) {
            root.style.setProperty(prop, value);
        }
    }
}

export type ColorScheme = 'light' | 'dark' | 'system';

/**
 *
 */
export function setColorScheme(scheme: ColorScheme, root: HTMLElement = document.documentElement): void {
    if (scheme === 'system') {
        root.removeAttribute('data-theme');
    } else {
        root.setAttribute('data-theme', scheme);
    }
}
